import { Box, Stack, Typography, useTheme } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import {
  Header,
  PieChart,
  SumChart,
  Table,
} from 'components/features/Dashboard';
import SecuredImage from 'components/hooks/SecuredImage';
import { useClusterDbSocketContext } from 'components/hooks/useClusterDbSocketContext';
import { API_PATHS } from 'constant/api';
import { GENDER } from 'constant/enum';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';
import {
  convertDateUTCToLocal,
  DatePattern,
  getKeyFromLocalStorage,
} from 'utils/constant';
import { getVisits } from 'utils/fetch';
import ja from 'utils/ja';

type AnalyzeResult = {
  visitCount: number;
  shotCount: number;
  uniqueCount: number;
};

interface DataPieChartType {
  id: number;
  value: number;
  label: string;
}

const analyzeDataVisit = (data: IReferenceVisit[]) => {
  let visitCount = 0;
  let shotCount = 0;
  let maleCount = 0;
  let femaleCount = 0;
  const ageCount = [0, 0, 0, 0, 0, 0, 0];
  const uniqueCount = data.length ?? 0;

  if (!uniqueCount) {
    return {
      visitCount,
      shotCount,
      uniqueCount,
      maleCount,
      femaleCount,
      ageCount,
    };
  }

  data.forEach((person) => {
    visitCount += person.visits.length;

    person.visits.forEach((visit) => {
      visit.pointViews.forEach((pv) => {
        shotCount += pv.shots.length;
      });
    });

    if (person.gender === 'male') {
      maleCount++;
    } else if (person.gender === 'female') {
      femaleCount++;
    }

    if (person.age < 10) {
      ageCount[0]++;
    } else if (person.age < 20) {
      ageCount[1]++;
    } else if (person.age < 30) {
      ageCount[2]++;
    } else if (person.age < 40) {
      ageCount[3]++;
    } else if (person.age < 50) {
      ageCount[4]++;
    } else if (person.age < 60) {
      ageCount[5]++;
    } else {
      ageCount[6]++;
    }
  });

  return {
    visitCount,
    shotCount,
    uniqueCount,
    maleCount: Math.round((maleCount / uniqueCount) * 100),
    femaleCount: Math.round((femaleCount / uniqueCount) * 100),
    ageCount: ageCount.map((age) => Math.round((age / uniqueCount) * 100)),
  };
};

const formatDuration = (startTime: string, endTime: string): string => {
  const start = dayjs(startTime);
  const end = dayjs(endTime);

  const diffMinutes = end.diff(start, 'minute');
  const hours = Math.floor(diffMinutes / 60);
  const remainingMinutes = diffMinutes % 60;

  return hours ? `${hours}時間${remainingMinutes}分` : `${remainingMinutes}分`;
};

const Dashboard = () => {
  const theme = useTheme();
  const { eventId, reconnect, isConnected } = useClusterDbSocketContext();

  const [value, setValue] = useState<Dayjs | null>(dayjs());
  const [references, setReferences] = useState<IReferencesRes[]>([]);
  const [dataCount, setDataCount] = useState<AnalyzeResult | null>(null);
  const [dataChart, setDataChart] = useState<{
    gender: DataPieChartType[];
    age: DataPieChartType[];
  }>({
    gender: [],
    age: [],
  });
  const [page] = useState<{ currentPage: number; totalPage: number }>({
    currentPage: 1,
    totalPage: 1,
  });

  const handleGetReference = async () => {
    const start = dayjs(value).startOf('day');
    const end = dayjs(value).endOf('day');
    const search = {
      startTime: start.utc().format(DatePattern.UTC_DATE),
      endTime: end.utc().format(DatePattern.UTC_DATE),
    };

    try {
      const appId = getKeyFromLocalStorage('appId');
      const appKey = getKeyFromLocalStorage('appKey');
      const dbId = getKeyFromLocalStorage('dbId');
      const dbKey = getKeyFromLocalStorage('dbKey');

      const { data } = await getVisits(search, {
        appId: appId ?? '',
        appKey: appKey ?? '',
        dbId: dbId ?? '',
        dbKey: dbKey ?? '',
      });

      const visitRes: IReferencesRes[] = [];

      for (const ref of data) {
        for (const visit of ref.visits) {
          visitRes.push({
            id: ref.id,
            age: ref.age,
            gender:
              ref.gender === GENDER.MALE
                ? ja.dashboard.male
                : ja.dashboard.female,
            shortId: ref.id.slice(-4),
            duration: formatDuration(visit.startTime, visit.endTime),
            endTime: convertDateUTCToLocal(
              visit.endTime,
              DatePattern.DATE_TIME
            ),
          });
        }
      }

      setReferences(
        visitRes.sort((prev: IReferencesRes, next: IReferencesRes) => {
          const timePrev = dayjs(prev.endTime).valueOf();
          const timeNext = dayjs(next.endTime).valueOf();
          return timeNext - timePrev;
        })
      );

      const resultChart = analyzeDataVisit(data);

      setDataCount({
        shotCount: resultChart.shotCount,
        uniqueCount: resultChart.uniqueCount,
        visitCount: resultChart.visitCount,
      });

      setDataChart({
        gender:
          !resultChart.maleCount && !resultChart.femaleCount
            ? []
            : [
                {
                  id: 0,
                  value: resultChart.maleCount,
                  label: ja.dashboard.male,
                },
                {
                  id: 1,
                  value: resultChart.femaleCount,
                  label: ja.dashboard.female,
                },
              ],
        age: !resultChart.ageCount.every((num: number) => num === 0)
          ? [
              {
                id: 0,
                value: resultChart.ageCount[0],
                label: '子供',
              },
              {
                id: 1,
                value: resultChart.ageCount[1],
                label: '10代',
              },
              {
                id: 2,
                value: resultChart.ageCount[2],
                label: '20代',
              },
              {
                id: 3,
                value: resultChart.ageCount[3],
                label: '30代',
              },
              {
                id: 4,
                value: resultChart.ageCount[4],
                label: '40代',
              },
              {
                id: 5,
                value: resultChart.ageCount[5],
                label: '50代',
              },
              {
                id: 6,
                value: resultChart.ageCount[6],
                label: '60以上',
              },
            ]
          : [],
      });
    } catch (error) {
      // empty
    }
  };

  useEffect(() => {
    handleGetReference();
  }, [value, eventId]);

  // Reconnect socket when Home component mounts (in case dbId/dbKey was set from Setting)
  useEffect(() => {
    const dbId = getKeyFromLocalStorage('dbId');
    const dbKey = getKeyFromLocalStorage('dbKey');

    if (dbId && dbKey && isConnected === false) {
      reconnect();
    }
  }, [isConnected]);

  const dataTable = references.map((row) => ({
    ...row,
    imageUrl: (
      <Box sx={{ display: 'flex' }}>
        <SecuredImage
          src={`${API_PATHS.get_reference_image(row.id)}`}
          alt='face'
          style={{
            width: 48,
            height: 48,
            objectFit: 'cover',
            border: '1px solid #000000',
          }}
        />
      </Box>
    ),
    endTime: (
      <Typography
        sx={{
          color: theme.palette.primary.main,
          fontWeight: '700',
        }}
      >
        {row?.endTime}
      </Typography>
    ),
    duration: (
      <Typography
        sx={{
          color: theme.palette.primary.main,
          fontWeight: '700',
        }}
      >
        {row?.duration}
      </Typography>
    ),
  }));

  return (
    <Stack flexDirection='row' sx={{ height: '100vh' }}>
      <Box
        sx={{
          width: '538px',
          padding: '10px',
          borderRight: '1px solid #BABABA',
          '& *': {
            fontSize: '40px !important',
          },
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: '10px',
            backgroundColor: '#ccc',
          },
        }}
      >
        <Header />
        <Box mb='10px'>
          <DatePicker
            value={value}
            onChange={(newValue: Dayjs | null) => {
              setValue(newValue);
              page.currentPage = 1;
            }}
            slotProps={{
              textField: {
                variant: 'outlined',
                fullWidth: true,
                sx: {
                  '& .MuiInputBase-input': {
                    padding: '0 14px',
                    color: '#1a1a1a',
                  },
                },
              },
            }}
            format='YYYY年MM月DD日'
          />
        </Box>
        {dataCount && <SumChart dataCount={dataCount} />}
        <Box
          sx={{
            borderRadius: '8px',
            border: '0.5px solid #BABABA',
            boxshadow: '0px 2px 2px 0px #00000040',
            mt: '10px',
          }}
        >
          <PieChart
            title={ja.dashboard.gender}
            colors={[theme.palette.primary.main, '#F564A9']}
            data={dataChart.gender}
            label={[
              { color: theme.palette.primary.main, label: ja.dashboard.male },
              { color: '#F564A9', label: ja.dashboard.female },
            ]}
          />
        </Box>
        <Box
          sx={{
            borderRadius: '8px',
            border: '0.5px solid #BABABA',
            boxshadow: '0px 2px 2px 0px #00000040',
            mt: '10px',
          }}
        >
          <PieChart
            title={ja.dashboard.age}
            colors={[
              '#7DC9F7',
              '#4BB0F4',
              '#2996F0',
              '#2180CC',
              '#1A69A6',
              '#135280',
              '#135280',
            ]}
            data={dataChart.age}
            label={[
              { color: '#7DC9F7', label: '子供' },
              { color: '#4BB0F4', label: '10代' },
              { color: '#2996F0', label: '20代' },
              { color: '#2180CC', label: '30代' },
              { color: '#1A69A6', label: '40代' },
              { color: '#135280', label: '50代' },
              { color: '#135280', label: '60以上' },
            ]}
          />
        </Box>
      </Box>
      <Box
        id='lazy-load'
        sx={{
          flex: 1,
          backgroundColor: '#F8F9FA',
          '& *': {
            fontSize: '40px !important',
          },
          padding: '20px',
          maxHeight: '100vh',
          overflowY: 'auto',
          position: 'relative',
        }}
      >
        <Table
          headers={[
            { key: 'imageUrl', title: ja.dashboard.image },
            { key: 'shortId', title: ja.dashboard.id_number },
            { key: 'gender', title: ja.dashboard.gender, align: 'center' },
            { key: 'age', title: ja.dashboard.age, align: 'center' },
            { key: 'endTime', title: ja.dashboard.timestamp },
            { key: 'duration', title: ja.dashboard.stay_time, align: 'center' },
          ]}
          data={dataTable}
        />
      </Box>
    </Stack>
  );
};

export default Dashboard;
